import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, ExternalLink, HandCoins, Info, Loader2, ReceiptText } from "lucide-react";
import dayjs from "dayjs";
import type { AppointmentItem } from "../api/Appointment";
import useAuthStore from "../store/authStore";
import { isTechnicianAccount } from "../utils/accountRole";
import { getPosOrder, verifyPosCheckin, type PosOrderDetail } from "../api/PosV2";
import { CONFIRMED_TAG_STATUS, appointmentLines, compareWithAppointment, linesTotal } from "../pages/POS/appointmentLink";
import { PAYMENT_STATUS_LABEL, POS_STATUS_LABEL, orderFromApi, parseViDateTime } from "../pages/POS/remote";
import { StatusPill, money } from "../pages/POS/utils";
import AppointmentDiff from "../pages/POS/components/AppointmentDiff";
import RemoteOrderModal from "../pages/POS/components/RemoteOrderModal";

/**
 * Tình trạng thanh toán tại quầy của lịch hẹn (API POS V2).
 * Tư vấn chuyển lịch hẹn sang "S7 – Xác nhận làm" → lịch hẹn vào danh sách "Chờ thanh toán" trên máy POS.
 * Thu ngân thu tiền tạo đơn POS; ô này tìm đơn của lịch hẹn qua API đối soát (checkin/verify theo appointment_id),
 * hiện công nợ, trạng thái vào phòng và chênh lệch so với lịch hẹn.
 */

interface Props {
    appointment: AppointmentItem | null;
}

const STEPS = ["Tư vấn", "Xác nhận làm", "Đã thu tiền", "Đang làm dịch vụ"];

const PAY_STATUS_CLS: Record<string, string> = {
    PAID: "bg-green-50 text-green-700 border-green-200",
    PARTIALLY_PAID: "bg-amber-50 text-amber-700 border-amber-200",
    UNPAID: "bg-red-50 text-red-600 border-red-200",
};

export default function AppointmentPosOrder(props: Props) {
    // Kỹ thuật viên không xem số tiền (PA, P12)
    return isTechnicianAccount() ? null : <PosOrderBlock {...props} />;
}

function PosOrderBlock({ appointment }: Props) {
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canOpenPos = isSuperAdmin || hasPermission("pos.create");
    const aptId = appointment?.id;
    const [order, setOrder] = useState<PosOrderDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [viewOpen, setViewOpen] = useState(false);
    const lines = useMemo(() => appointmentLines(appointment?.services), [appointment?.services]);

    const load = useCallback(async () => {
        if (!aptId) return;
        setLoading(true);
        setError("");
        // Đối soát theo appointment_id: 404 nghĩa là lịch hẹn chưa có đơn POS
        const found = await verifyPosCheckin({ appointment_id: aptId });
        if (!found.ok || !found.data) {
            setOrder(null);
            setLoading(false);
            return;
        }
        const detail = await getPosOrder(found.data.order_id);
        setLoading(false);
        if (!detail.ok || !detail.data) {
            setOrder(null);
            setError(`Lịch hẹn đã có đơn ${found.data.order_code} nhưng chưa tải được chi tiết: ${detail.message}`);
            return;
        }
        setOrder(detail.data);
    }, [aptId]);

    useEffect(() => { load(); }, [load]);

    const diff = useMemo(() => {
        if (!order) return null;
        const { order: o } = orderFromApi(order);
        // Dòng đơn (2b) không có service_id → ghép với dịch vụ lịch hẹn theo tên để so chênh lệch
        const key = (s: string) => s.normalize("NFC").trim().toLowerCase();
        const orderLines = o.lines.map((l) => ({ ...l, aptServiceId: lines.find((a) => key(a.name) === key(l.name))?.serviceId }));
        return compareWithAppointment({ lines, total: linesTotal(lines) }, orderLines, o.totals);
    }, [order, lines]);

    if (!appointment) return null;

    const tagStatus = Number(appointment.customer_tag_status ?? appointment.channel_customer?.customer_tag_status ?? 0);
    const confirmed = tagStatus === CONFIRMED_TAG_STATUS;
    const step = order ? (order.pos_status === "IN_PROGRESS" ? 3 : 2) : confirmed ? 1 : 0;
    const total = linesTotal(lines);
    const orderAt = order ? parseViDateTime(order.order_date_iso || order.created_at_iso || order.payments?.[0]?.created_at_iso || order.payments?.[0]?.created_at) : "";
    const cashier = order?.payments?.[0]?.cashier_name;

    return (
        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6 mb-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-slate-100 dark:border-[#2a2a2a] pb-3.5 mb-4">
                <div className="title-info gap-2 text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                    <ReceiptText size={16} className="text-[#4e91ab]" />
                    Thanh toán tại quầy (POS)
                </div>
                <ol className="flex flex-wrap items-center gap-1.5 text-xs" aria-label="Tiến trình thanh toán">
                    {STEPS.map((label, i) => {
                        const doneStep = i < step || (i === step && step >= 2);
                        return (
                            <li key={label} className="flex items-center gap-1.5">
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                                        doneStep
                                            ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                            : i === step
                                              ? "bg-[#4e91ab]/10 text-[#2c6b7f] dark:text-[#7cc4da]"
                                              : "bg-slate-100 text-slate-400 dark:bg-[#2a2a2a]"
                                    }`}
                                    aria-current={i === step ? "step" : undefined}
                                >
                                    {doneStep && <CheckCircle2 size={12} />}
                                    {label}
                                </span>
                                {i < STEPS.length - 1 && <ArrowRight size={12} className="text-slate-300" />}
                            </li>
                        );
                    })}
                </ol>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Đang tải đơn POS của lịch hẹn…</div>
            ) : error ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <span>{error}</span>
                    <button onClick={load} className="shrink-0 font-semibold underline">Thử lại</button>
                </div>
            ) : order ? (
                <div className="rounded-lg border border-slate-200 dark:border-[#2a2a2a] p-3 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <button onClick={() => setViewOpen(true)} className="font-mono font-semibold text-[#2c6b7f] hover:underline">
                                {order.order_code}
                            </button>
                            <StatusPill
                                label={PAYMENT_STATUS_LABEL[order.payment_status] || order.payment_status}
                                cls={PAY_STATUS_CLS[order.payment_status] || "border-slate-200 bg-slate-50 text-slate-600"}
                            />
                            <StatusPill label={POS_STATUS_LABEL[order.pos_status] || order.pos_status} cls="border-slate-200 bg-slate-50 text-slate-600" />
                            <span className="text-xs text-slate-500">
                                {cashier ? `Thu ngân ${cashier}` : ""}{orderAt ? ` · ${dayjs(orderAt).format("DD/MM HH:mm")}` : ""}
                            </span>
                        </div>
                        <button onClick={() => setViewOpen(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#2c6b7f] hover:underline">
                            Xem đơn <ExternalLink size={12} />
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                        <div><div className="text-xs text-slate-500">Tổng đơn</div><b>{money(order.final_amount)} ₫</b></div>
                        <div><div className="text-xs text-slate-500">Đã thu</div><b>{money(order.paid_amount)} ₫</b></div>
                        <div>
                            <div className="text-xs text-slate-500">Còn nợ</div>
                            <b className={order.debt_amount > 0 ? "text-red-600" : ""}>{money(order.debt_amount)} ₫</b>
                        </div>
                    </div>
                    {order.debt_amount > 0 && (
                        <button onClick={() => setViewOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-sm font-semibold">
                            <HandCoins size={14} /> Thu nợ {money(order.debt_amount)} ₫
                        </button>
                    )}
                    {diff && <AppointmentDiff diff={diff} />}
                </div>
            ) : confirmed ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 dark:bg-amber-900/10 p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-amber-900 dark:text-amber-200">
                            <Clock size={14} />
                            Khách đã xác nhận làm — đang ở danh sách <b>Chờ thanh toán</b> trên máy POS
                        </span>
                        <span className="font-bold text-amber-900 dark:text-amber-200">{money(total)} ₫</span>
                    </div>
                    <p className="mt-1.5 text-xs text-amber-800/80 dark:text-amber-300/80">
                        Thu ngân điều chỉnh dịch vụ, KTV, chiết khấu tại quầy; số tiền cuối cùng theo đơn POS.
                    </p>
                    {canOpenPos && <Link
                        to={`/pos?appointment=${appointment.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#4e91ab] text-[#2c6b7f] dark:text-[#7cc4da] hover:bg-[#4e91ab]/10 text-sm font-semibold"
                    >
                        <ReceiptText size={14} /> Mở POS thu tiền
                    </Link>}
                </div>
            ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400 flex items-start gap-2">
                    <Info size={16} className="mt-0.5 shrink-0 text-[#4e91ab]" />
                    <span>
                        Khi khách đồng ý làm, chuyển trạng thái sang <b>S7 – Xác nhận làm</b>. Lịch hẹn sẽ tự hiện ở danh sách
                        <b> Chờ thanh toán</b> trên máy POS để thu ngân thu tiền ({money(total)} ₫ theo lịch hẹn).
                    </span>
                </p>
            )}

            {order && (
                <RemoteOrderModal open={viewOpen} orderKey={viewOpen ? order.order_id : null} onClose={() => { setViewOpen(false); load(); }} />
            )}
        </div>
    );
}
